import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const strength = useMemo(() => scorePassword(pw), [pw]);
  const canSubmit = pw.length >= 8 && pw === confirm && !busy && !missingToken;

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate("/login"), 4000);
      return () => clearTimeout(t);
    }
  }, [done, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      toast.success("Password updated. You can now sign in.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <SEO title="Reset password" description="Choose a new password for your Axiony account." path="/reset-password" />
      <div className="hidden lg:block relative grid-bg border-r border-white/10">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative p-16 h-full flex flex-col justify-between">
          <div className="mono-label">choose a new password</div>
          <div>
            <h2 className="font-display text-4xl font-semibold tracking-tight max-w-md">
              A fresh key, delivered securely.
            </h2>
            <p className="mt-4 text-slate-400 max-w-md">
              Use at least 8 characters. Once you reset, all active sessions and trusted devices are signed out for safety.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {done ? (
          <div className="w-full max-w-md space-y-5" data-testid="reset-done-panel">
            <div className="h-12 w-12 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-semibold">Password updated</h1>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                Your Axiony password has been reset. All other sessions have been signed out. Redirecting you to the sign-in page…
              </p>
            </div>
            <Link to="/login">
              <Button className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg" data-testid="go-to-login-btn">
                Continue to sign in
              </Button>
            </Link>
          </div>
        ) : missingToken ? (
          <div className="w-full max-w-md space-y-4" data-testid="missing-token-panel">
            <h1 className="font-display text-3xl font-semibold">Invalid reset link</h1>
            <p className="text-sm text-slate-400">
              This URL is missing its reset token. Request a new link from the forgot password page.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg" data-testid="request-new-link-btn">
                Request a new link
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="reset-password-form">
            <Link to="/login" className="text-sm text-slate-400 hover:text-white flex items-center gap-1" data-testid="reset-back-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>

            <div>
              <div className="mono-label flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-[#06B6D4]" /> secure reset
              </div>
              <h1 className="font-display text-3xl font-semibold mt-2">Choose a new password</h1>
              <p className="text-sm text-slate-400 mt-2">
                Use at least 8 characters. Mixing letters, numbers and symbols makes it much harder to guess.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pw" className="text-slate-300">New password</Label>
              <Input
                id="pw"
                type="password"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="reset-password-input"
                className="bg-[#111827] border-white/10 text-slate-100"
              />
              {pw && <StrengthMeter score={strength} />}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-slate-300">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                className="bg-[#111827] border-white/10 text-slate-100"
              />
              {confirm && confirm !== pw && (
                <div className="text-xs text-[#F87171]">Passwords do not match.</div>
              )}
            </div>

            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="reset-submit-btn"
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg py-6"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

const scorePassword = (pw) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
};

const StrengthMeter = ({ score }) => {
  const labels = ["Too short", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#F87171", "#F87171", "#F59E0B", "#3B82F6", "#10B981"];
  return (
    <div className="flex items-center gap-2" data-testid="password-strength">
      <div className="flex-1 flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full"
            style={{ background: i < score ? colors[score] : "rgba(255,255,255,0.08)" }}
          />
        ))}
      </div>
      <span className="text-xs" style={{ color: colors[score] }}>{labels[score]}</span>
    </div>
  );
};

export default ResetPassword;
